import { Configuration, EnvironmentConfiguration } from '../../configuration';

import { Entity, EntityLink } from '../entity';
import { ActivityEntityName } from './activity.entity.name';
import { ActivityShareType, ActivitySource, Shareable } from './activity';
import { BackendNotificationMessage } from '../sockets';
import { ActivityKind } from './activity.type';
import { TaskKind } from '../tasks';
import { DateUtility } from '../../utilities/date.utility';


export type AttachmentType = 'File' | 'Image' | 'Video' | 'Form' | 'Signature';

export type AttachmentKind = ActivityKind | TaskKind;;

export interface Attachment {
    _id?: string;
    parentId?: string;
    entityId?: string;
    entityName?: string | ActivityEntityName;
    kind?: AttachmentKind;
    type?: AttachmentType;
    title?: string;
    description?: string;
    content?: string;
    path?: string;
    data?: any;
    fileName?: string;
    createdDate?: Date;
    createdBy?: string;
    thumbnail?: string;
    integration?: AttachmentIntegration;
    claimReference?: Entity;
    tags?: Entity[];

    /** This is a temporary flag to indicate an attachment is syncing  */
    syncing?: boolean;

    updatedAt?: Date;
}

export interface MetadataAttachment extends Attachment {
    sensitive?: boolean;
    shared?: ActivityShareType[];
    systemSharing?: boolean;

    effectiveSharing?: Shareable;

    metadata?: Entity[];
    isFile?: boolean;
    source?: ActivitySource

    entityId?: string;
}

export interface AttachmentIntegration {
    _id: string;
    error: string;
}

export interface File extends Entity {
    attachment: Attachment;
    links: EntityLink[];
    tags: Entity[];
    createdBy: Entity;
    createdAt: Date;
}

export interface TagCount {
    tag: Entity;
    count: number;
}

export interface FileDashboardResponse {
    tags: TagCount[];
    files: File[];
}

export interface AttachmentBlobCopyRequest {

    /** This is the id of the attachment to copy */
    _id: string;

    /** This is the id of the parent entity that owns the attachment */
    parentId: string;

    /** This is the type of the parent entity */
    parentType: 'Activity' | 'Task';

    /** This is the name of the container to copy the blob to */
    destinationContainer?: string;

    /** This is the name of the blob in the destination container, by default it will be the same as the source */
    fileName?: string;

    /** This is the name of the tenant that owns the original blob */
    notification?: BackendNotificationMessage;
}

export interface AttachmentBlobExternalCopyRequest {
    /** This is the id of the attachment to copy */
    _id: string;

    /** This is the id of the parent entity that owns the attachment */
    parentId: string;

    /** This is the type of the parent entity */
    parentType: 'Activity';

    /** This is the source url of the attachment */
    sourceUrl: string;

    /** The file name to be copied, helps determine destination */
    fileName: string;
}

export function getExtension(url: string) {
    const parts = url.split('.');
    return parts[parts.length - 1];
}

export function getFilename(url: string) {
    const parts = url.split('/');
    const filename = parts[parts.length - 1];
    return filename;
}

export function getFilenameWithoutExtension(url: string) {
    const filename = getFilename(url);
    const extension = getExtension(filename);
    return filename.replace(`.${extension}`, '').trim();

    // const parts = url.split('/');
    // const filename = parts[parts.length - 1];
    // const extension = getExtension(filename);
    // return filename.replace(`.${extension}`, '').trim();
}

export function getExtensionFromMimeType(mimeType: string) {
    const imageMimeToFileExtension = {
        'image/jpeg': 'jpg', 'image/jpg': 'jpg', 'image/png': 'png', 'image/gif': 'gif',
        'image/webp': 'webp', 'image/tiff': 'tif', 'image/bmp': 'bmp', 'image/x-icon': 'ico',
        'image/svg+xml': 'svg', 'image/heif': 'heif', 'image/heic': 'heic', 'image/avif': 'avif'
    };
    const extension = imageMimeToFileExtension[mimeType] ?? '';
    return extension;
}

export function getFileThumbnailUrl(attachment: Attachment, cacheBust = false): string {

    if (attachment.type !== 'File') {
        if (attachment.type !== 'Form') {
            return attachment.thumbnail;
        }

        if (!attachment.path) {
            return attachment.thumbnail;
        }
    }

    const path = attachment.path;
    const fileName = getFilenameWithoutExtension(path);
    const baseUrl = path.substring(0, path.indexOf('/files')) + '/thumbnails';
    let cacheBuster = '';
    if (cacheBust && attachment.updatedAt) {
        cacheBuster = `?v=${DateUtility.getDateTime(attachment.updatedAt).toISO()}`;
    }

    return `${baseUrl}/${fileName}.webp${cacheBuster}`;

}

export function getFormThumbnailUrl(attachment: Attachment, config: EnvironmentConfiguration): string {

    if (attachment.type !== 'Form')
        return null;

    let safeFormUrl = ''

    if ((attachment.data as string)?.endsWith('pdf')) {

        // we probably have a form from Activities
        const path = attachment.data;
        const fileName = getFilenameWithoutExtension(path);
        const baseUrl = path.substring(0, path.indexOf('/files')) + '/thumbnails';

        safeFormUrl = `${baseUrl}/${fileName}.webp`;

    } else {
        const { tenant, environment } = config;

        // we probably just a form response hash key 
        safeFormUrl = `https://jmsfiles.blob.core.windows.net/${environment}-${tenant}/thumbnails/${attachment._id}.webp`;
    }

    // Alwayse use cache burster for forms (this is usually apple for form that didn't come from Activity)
    safeFormUrl = applyCacheBurster(safeFormUrl, attachment.updatedAt);

    return safeFormUrl;
}

/**
 * Get PDF Attachment url on storage
 * @param fileName file name without extension. E.g invoice number, quote number, ...
 * @param config tenant configuration
 * @returns 
 */
export function getPdfAttachmentUrl(fileName: string, config: Configuration): string {

    const { tenant, environment } = config;

    return `https://jmsfiles.blob.core.windows.net/${environment}-${tenant}/files/${fileName}.pdf`;
}

export function applyCacheBurster(url: string, updatedAt: Date): string {
    let updated = url;
    if (updatedAt) {
        updated += `?v=${DateUtility.getDateTime(updatedAt).toISO()}`;
    }
    else {
        updated = `${updated}?v=${DateUtility.getDateTime(Date.now()).toISO()}`
    }

    return updated;
}

export function enableCacheBurster(attachment: Attachment): boolean {
    const useCacheBurster = attachment.kind === ActivityKind.Form || (attachment.entityName && [ActivityEntityName.Invoice, ActivityEntityName.QuotePro].includes(attachment.entityName as ActivityEntityName));
    return useCacheBurster;
}